// Lead Insights (Module F) — the Lead's private per-teacher picture for training/meetings. LEAD-ONLY
// route. Built from the SHARED read-model (Tier-1 work) + the Lead-private `lead_tracking` notes
// (People_Progress: last review · last observation · current focus · next action). Never team-visible;
// no quality grade, no ranking — on-time is a calm coordination stat, late = a quiet time. One-thumb.
// JOB-W01: each teacher's TAUGHT evidence (their own confirmations) sits on its own line beside the
// plan counts — sessions and distinct class+step pairs — and each timeline lesson shows its teaching
// state separately from its plan status. The Lead reads it; only the teacher records it.

import { api } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";
import { pageHeader } from "../ui";
import { COACH_SET_ASIDE } from "../nav";

type LessonStatus = "not_started" | "in_progress" | "submitted" | "approved";

interface Rollup {
  teacher_id: string;
  teacher_name: string;
  total: number;
  submitted: number;
  approved: number;
  drafts: number;
  on_time: number;
  on_time_rate: number | null;
  last_submitted_at: string | null;
  taught_sessions: number;
  taught_distinct_nodes: number; // different curriculum steps across the teacher's classes
  taught_class_nodes: number; // the same step in two classes counts twice here
  taught_freeform_sessions: number;
  withdrawn_sessions: number;
}
interface TimelineRow {
  class_name: string;
  class_id: string;
  lesson_id: string;
  topic: string;
  week_no: number;
  node_id: string | null;
  status: LessonStatus;
  on_time: boolean | null;
  submitted_at: string | null;
  plan_id: string | null;
  teaching_state: "not_recorded" | "taught" | "confirmation_withdrawn";
  taught_confirmed_at: string | null;
  // JOB-W02 · the session's trail, as counts. Neither is teaching evidence.
  chosen_activities: number;
  session_papers: number;
  // Tier-2 — present only on this Lead-private surface, written by the Tier-2 Worker (③).
  feedback?: { patterns: string[]; created_at: string } | null;
}
interface Tracking {
  teacher_id: string;
  last_review: string | null;
  last_observation: string | null;
  current_focus: string | null;
  next_action: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
interface InsightsResp { term: { name: string; week_count: number } | null; teachers: Rollup[]; timeline?: TimelineRow[] }
interface ObsRow {
  id: string;
  kind: "classroom" | "marking";
  observed_on: string;
  class_label: string | null;
  status: "draft" | "finalized" | "acknowledged";
}

// Lessons are created 'in_progress' (functions/api/lessons.ts) and never sit at 'not_started',
// so a "Not started" filter could only ever return empty — it is intentionally omitted here.
const STATUS_OPTS: { key: "" | LessonStatus; label: string }[] = [
  { key: "", label: "All statuses" },
  { key: "submitted", label: "Submitted" },
  { key: "approved", label: "Approved" },
  { key: "in_progress", label: "In progress" },
];

function statusChip(status: LessonStatus, onTime: boolean | null): string {
  if (status === "approved") return `<span class="chip approved">Approved ✓</span>`;
  if (status === "submitted") {
    const late = onTime === false ? ` <span class="muted">(late)</span>` : "";
    return `<span class="chip submitted">Submitted</span>${late}`;
  }
  return `<span class="chip">Draft</span>`;
}

// Taught = the teacher's own confirmation. Kept on a separate line from planned/submitted/approved.
// Steps are DIFFERENT curriculum steps; when the same step was taught in more than one class, the
// per-class figure is added so two same-grade classes never read as double coverage.
function taughtLine(r: Rollup): string {
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  // W01 review N3: "1 different curriculum step" reads oddly — there is nothing to differ from.
  const steps = r.taught_distinct_nodes === 1 ? "1 curriculum step" : plural(r.taught_distinct_nodes, "different curriculum step");
  const perClass = r.taught_class_nodes > r.taught_distinct_nodes
    ? ` — ${r.taught_class_nodes} when each class counts separately`
    : "";
  const freeform = r.taught_freeform_sessions ? ` (+${r.taught_freeform_sessions} not linked to a step)` : "";
  const withdrawn = r.withdrawn_sessions ? ` · ${r.withdrawn_sessions} withdrawn` : "";
  return `<span data-taught="${r.taught_sessions}" data-taught-steps="${r.taught_distinct_nodes}">Taught (teacher-confirmed): ${plural(r.taught_sessions, "session")} · ${steps}${perClass}${freeform}${withdrawn}</span>`;
}

function teachingChip(l: TimelineRow): string {
  if (l.teaching_state === "taught") return ` <span class="chip taught">Taught ✓</span>`;
  if (l.teaching_state === "confirmation_withdrawn") return ` <span class="chip withdrawn">Confirmation withdrawn</span>`;
  return ""; // not recorded: no chip — same calm absence as the class page; the hint above explains it
}

function rate(r: Rollup): string {
  if (r.on_time_rate === null) return `<span class="muted">no submissions yet</span>`;
  return `on-time ${Math.round(r.on_time_rate * 100)}%`;
}

export async function renderInsights(app: HTMLElement): Promise<void> {
  // Page-local state (closure; reset on re-entry).
  let term: { name: string; week_count: number } | null = null;
  let teachers: Rollup[] = [];
  let timeline: TimelineRow[] = [];
  let tracking: Tracking | null = null;
  let observations: ObsRow[] = [];
  let selectedId = "";       // "" = overview; otherwise a teacher_id (drill view)
  let statusFilter = "";     // "" = all statuses
  let weekFrom = "";         // "" = from the start of term
  let weekTo = "";           // "" = to the end of term
  let topicQ = "";           // "" = any topic
  let err: string | null = null;
  let flash = "";

  // The current filters as a query string — shared by the insights load and the CSV export link.
  function filterParams(): URLSearchParams {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (weekFrom) qs.set("from", weekFrom);
    if (weekTo) qs.set("to", weekTo);
    if (topicQ) qs.set("q", topicQ);
    if (selectedId) qs.set("teacher", selectedId);
    return qs;
  }

  async function load(): Promise<void> {
    const qs = filterParams();
    const q = qs.toString() ? `?${qs}` : "";

    if (selectedId) {
      const [ins, trk, obs] = await Promise.all([
        api<InsightsResp>(`/api/lead/insights${q}`),
        api<{ tracking: Tracking | null }>(`/api/lead/tracking/${encodeURIComponent(selectedId)}`),
        api<{ observations: ObsRow[] }>(`/api/observations?teacher=${encodeURIComponent(selectedId)}`),
      ]);
      term = ins.term;
      teachers = ins.teachers;
      timeline = ins.timeline ?? [];
      tracking = trk.tracking;
      observations = obs.observations;
    } else {
      const ins = await api<InsightsResp>(`/api/lead/insights${q}`);
      term = ins.term;
      teachers = ins.teachers;
      timeline = [];
      tracking = null;
      observations = [];
    }
  }

  // ── filter bar (never printed — the print artifact is the picture, not the controls) ──
  function weekOpts(label: string, current: string): string {
    const n = term?.week_count ?? 0;
    let opts = `<option value="">${label}</option>`;
    for (let w = 1; w <= n; w++) opts += `<option value="${w}"${String(w) === current ? " selected" : ""}>W${w}</option>`;
    return opts;
  }

  function filterBar(): string {
    const teacherOpts =
      `<option value="">All teachers</option>` +
      teachers
        .map((t) => `<option value="${esc(t.teacher_id)}"${t.teacher_id === selectedId ? " selected" : ""}>${esc(t.teacher_name)}</option>`)
        .join("");
    const statusSel = STATUS_OPTS.map(
      (s) => `<option value="${s.key}"${s.key === statusFilter ? " selected" : ""}>${s.label}</option>`,
    ).join("");
    const exportHref = `/api/lead/export${filterParams().toString() ? `?${filterParams()}` : ""}`;
    return `
      <div class="row-actions no-print" style="gap:var(--space-3);margin-bottom:var(--space-3)">
        <select id="f-teacher" aria-label="Teacher" class="proj-move">${teacherOpts}</select>
        <select id="f-status" aria-label="Status" class="proj-move">${statusSel}</select>
        <select id="f-from" aria-label="From week" class="proj-move">${weekOpts("From week", weekFrom)}</select>
        <select id="f-to" aria-label="To week" class="proj-move">${weekOpts("To week", weekTo)}</select>
      </div>
      <div class="row-actions no-print" style="gap:var(--space-3);margin-bottom:var(--space-4)">
        <input id="f-q" type="search" aria-label="Search topic" placeholder="Search topic" value="${esc(topicQ)}"
               class="proj-move" style="flex:1;min-width:140px" />
        <a class="btn sm secondary" href="${esc(exportHref)}" download>Export CSV</a>
        <button class="btn sm secondary" type="button" data-act="print">Print</button>
      </div>`;
  }

  // ── overview (all teachers, rolled up) ───────────────────────────────────────────
  // "Take a wider look" — a calm, optional reflection aid (Bolman & Deal's four frames). A thinking
  // aid the Lead can open when a week feels busy or stuck — never a checklist, a score, or a metric.
  function framesPanel(): string {
    return `<details class="frames no-print">
      <summary>Take a wider look</summary>
      <p class="hint">Four calm lenses when a week feels busy or stuck — a thinking aid, not a checklist.</p>
      <ul class="frames-list">
        <li><strong>Structure</strong> — are roles &amp; coverage clear this week?</li>
        <li><strong>People</strong> — who needs support, or a word of recognition?</li>
        <li><strong>Politics</strong> — whose competing needs am I balancing?</li>
        <li><strong>Meaning</strong> — are we still connected to <em>why</em> we teach?</li>
      </ul>
    </details>`;
  }

  function overviewHtml(): string {
    const grid = teachers.length
      ? `<div class="lead-grid">${teachers
          .map(
            (t) =>
              `<button class="card" type="button" data-act="open" data-id="${esc(t.teacher_id)}" style="text-align:left">
             <strong>${esc(t.teacher_name)}</strong>
             <div class="muted" style="margin-top:4px;font-size:var(--text-sm)">
               ${t.total} lesson${t.total === 1 ? "" : "s"} · ${t.submitted} submitted · ${t.approved} approved · ${rate(t)}
             </div>
             <div class="muted" style="margin-top:2px;font-size:var(--text-sm)">${taughtLine(t)}</div>
           </button>`,
          )
          .join("")}</div>`
      : `<p class="empty">No lessons planned yet this term.</p>`;
    return `${framesPanel()}${grid}`;
  }

  // ── drill view (one teacher: timeline + tracking notes) ──────────────────────────
  // The 3 Tier-2 English-pattern notes (when the AI pass has run) — calm, developmental,
  // private to the Lead. Absent = nothing shown (a gap is never an error).
  function feedbackHtml(l: TimelineRow): string {
    if (!l.feedback?.patterns.length) return "";
    const items = l.feedback.patterns.map((p) => `<li>${esc(p)}</li>`).join("");
    return `
      <div class="muted" style="font-size:var(--text-sm);margin-top:8px">
        English patterns <span class="hint">· private to you</span>
        <ul style="margin:4px 0 0;padding-left:18px">${items}</ul>
      </div>`;
  }

  // What this session has beside its plan — counts only, and only when there is something to say.
  // A chosen activity and a saved paper are preparation, never evidence that the lesson was taught.
  function trailLine(l: TimelineRow): string {
    const parts = [
      l.chosen_activities ? `${l.chosen_activities} activit${l.chosen_activities === 1 ? "y" : "ies"} chosen` : "",
      l.session_papers ? `${l.session_papers} paper${l.session_papers === 1 ? "" : "s"}` : "",
    ].filter(Boolean);
    if (!parts.length) return "";
    return `<div class="muted" style="font-size:var(--text-sm);margin-top:4px" data-trail="${l.chosen_activities}/${l.session_papers}">${parts.join(" · ")}</div>`;
  }

  function timelineHtml(): string {
    if (!timeline.length) return `<p class="empty">No lessons${statusFilter ? " for this filter" : ""} yet.</p>`;
    return timeline
      .map((l) => {
        const inner = `<span class="chip">W${esc(l.week_no)}</span> <strong>${esc(l.class_name)}</strong> · ${esc(l.topic)}
          <div style="margin-top:6px">${statusChip(l.status, l.on_time)}${teachingChip(l)}</div>${trailLine(l)}${feedbackHtml(l)}`;
        // JOB-W02: the card opens the SESSION page (teacher+Lead tier) — the one place that carries
        // the plan, the activities chosen for this session and the papers prepared for it, each back
        // to its own source. A quiet link still jumps to the class. Two links, not nested.
        const open = `<a class="wk-open" data-link href="/lesson/${esc(l.lesson_id)}">${inner}</a>`;
        const classLink = `<a class="wk-class" data-link href="/classes/${esc(l.class_id)}">View class ›</a>`;
        return `<div class="card wk-lesson">${open}${classLink}</div>`;
      })
      .join("");
  }

  function trackingFormHtml(): string {
    const t = tracking;
    const updated = t?.updated_at ? `<p class="hint">Last updated ${esc(t.updated_at)}</p>` : "";
    return `
      <h2>Tracking notes <span class="muted">(private to you — never shown to the team)</span></h2>
      <p class="hint">A quiet note for yourself. Describe what happened and a next step — not a verdict.</p>
      ${updated}
      <form id="track">
        <label for="t-review">Last review</label>
        <input id="t-review" name="last_review" type="date" value="${esc(t?.last_review ?? "")}" />
        <label for="t-obs">Last observation</label>
        <input id="t-obs" name="last_observation" type="date" value="${esc(t?.last_observation ?? "")}" />
        <label for="t-focus">Current focus</label>
        <textarea id="t-focus" name="current_focus" placeholder="What this teacher is working on">${esc(t?.current_focus ?? "")}</textarea>
        <label for="t-next">Next action</label>
        <textarea id="t-next" name="next_action" placeholder="The next thing you'll do (observe, meet, follow up)">${esc(t?.next_action ?? "")}</textarea>
        <div style="height:12px"></div>
        <button class="btn no-print" type="submit">Save notes</button>
      </form>`;
  }

  // ── observations (the GTIS instruments — Lead-private until finalized) ───────────
  const OBS_KIND = { classroom: "Classroom observation", marking: "Book & worksheet check" } as const;
  function obsChip(s: ObsRow["status"]): string {
    if (s === "draft") return `<span class="chip">Draft</span>`;
    if (s === "finalized") return `<span class="chip submitted">Finalized</span>`;
    return `<span class="chip approved">Acknowledged ✓</span>`;
  }
  function observationsHtml(): string {
    const list = observations.length
      ? observations
          .map(
            (o) =>
              `<a class="card" data-link href="/observation/${esc(o.id)}">
                 ${OBS_KIND[o.kind]}${o.class_label ? ` · ${esc(o.class_label)}` : ""}
                 <div class="muted" style="font-size:var(--text-sm);margin-top:2px">${esc(o.observed_on)}</div>
                 <div style="margin-top:6px">${obsChip(o.status)}</div>
               </a>`,
          )
          .join("")
      : `<p class="empty">No observations yet for this teacher.</p>`;
    return `
      <h2>Observations</h2>
      <p class="hint">The school's observation & marking-check forms — drafts are private to you; finalize after the 1-on-1 talk.</p>
      ${list}
      <div class="row-actions no-print" style="margin-top:var(--space-2)">
        <button class="btn sm secondary" type="button" data-act="new-obs" data-kind="classroom">New classroom observation</button>
        <button class="btn sm secondary" type="button" data-act="new-obs" data-kind="marking">New book/worksheet check</button>
      </div>`;
  }

  function drillHtml(): string {
    const who = teachers.find((t) => t.teacher_id === selectedId);
    const name = who?.teacher_name ?? "Teacher";
    const stat = who
      ? `<p class="hint">${who.total} lesson${who.total === 1 ? "" : "s"} · ${who.submitted} submitted · ${who.approved} approved · ${rate(who)}</p>
         <p class="hint" data-drill-taught>${taughtLine(who)}</p>`
      : "";
    return `
      <p><a href="#" data-act="back" class="muted">← All teachers</a></p>
      <h2>${esc(name)}</h2>
      ${stat}
      <h2>Lessons this term</h2>
      <p class="hint">A partial picture — plans and timestamps, not the whole story of a week. "Taught" appears only when the teacher confirms it; a plan alone never counts.</p>
      ${timelineHtml()}
      ${COACH_SET_ASIDE ? "" : coachingHtml()}
      ${observationsHtml()}
      ${trackingFormHtml()}`;
  }

  // ── coaching sessions (Tier 3 — DR-001 one-way mirror; served by the READ-ONLY ──────────
  // Coaching-Review Worker, never this Pages app; every open/listen is audit-logged and the
  // teacher can see when you did). Loaded lazily after render — the section quietly says so.
  interface CoachListRow {
    id: string;
    created_at: string;
    ended_at: string | null;
    audio_duration_sec: number | null;
    shared_with_lead: boolean;
  }
  function coachingHtml(): string {
    return `
      <h2>Coaching sessions <span class="muted">(you and this teacher only)</span></h2>
      <p class="hint">Private to you and this teacher — never shown to the team. The talk-it-throughs are developmental, never a rating. Opening one is logged, and the teacher can see when you listened.</p>
      <div id="coachbox"><p class="muted">Loading…</p></div>`;
  }
  async function loadCoaching(): Promise<void> {
    const box = app.querySelector<HTMLElement>("#coachbox");
    if (!box || !selectedId) return;
    try {
      const tk = await api<{ ticket: string; review_url: string }>("/api/review/ticket", {
        method: "POST",
        body: JSON.stringify({ teacher_id: selectedId }),
      });
      const res = await fetch(`${tk.review_url}/teacher/${encodeURIComponent(selectedId)}/sessions?ticket=${encodeURIComponent(tk.ticket)}`);
      if (!res.ok) throw new Error("Coaching review isn't reachable right now.");
      const { sessions } = (await res.json()) as { sessions: CoachListRow[] };
      if (sessions.length === 0) {
        box.innerHTML = `<p class="empty">No talk-it-throughs yet.</p>`;
        return;
      }
      box.innerHTML = sessions
        .map((s) => {
          const mins = s.audio_duration_sec ? ` · ${Math.max(1, Math.round(s.audio_duration_sec / 60))} min` : "";
          const flag = s.shared_with_lead ? ` <span class="chip submitted">asked for grammar help</span>` : "";
          return `<div class="card" data-session="${esc(s.id)}" style="cursor:pointer">
            ${esc(s.created_at)}${mins}${flag}
            <div class="muted" style="font-size:var(--text-sm)">${s.ended_at ? "Tap to read · listen" : "In progress / unfinished"}</div>
            <div class="coach-detail" hidden></div>
          </div>`;
        })
        .join("");
      box.querySelectorAll<HTMLElement>("[data-session]").forEach((card) => {
        card.addEventListener("click", () => void openCoaching(card, tk));
      });
    } catch (ex) {
      box.innerHTML = `<p class="empty">${esc((ex as Error).message)}</p>`;
    }
  }
  async function openCoaching(card: HTMLElement, tk: { ticket: string; review_url: string }): Promise<void> {
    const detail = card.querySelector<HTMLElement>(".coach-detail")!;
    if (!detail.hidden) {
      detail.hidden = true;
      return;
    }
    detail.hidden = false;
    detail.innerHTML = `<p class="muted">Loading…</p>`;
    try {
      // Tickets are 5-minute; re-mint freely so an expired one never reads as an error.
      const fresh = await api<{ ticket: string; review_url: string }>("/api/review/ticket", {
        method: "POST",
        body: JSON.stringify({ teacher_id: selectedId }),
      });
      const id = card.dataset.session!;
      const res = await fetch(`${fresh.review_url}/session/${encodeURIComponent(id)}?ticket=${encodeURIComponent(fresh.ticket)}`);
      if (!res.ok) throw new Error("Couldn't open this session right now.");
      const { session } = (await res.json()) as {
        session: {
          transcript: string | null;
          summary: { insights?: string[]; key_points?: string[] } | null;
          corrected_script: string | null;
          has_audio: boolean;
        };
      };
      const audioTk = await api<{ ticket: string; review_url: string }>("/api/review/ticket", {
        method: "POST",
        body: JSON.stringify({ teacher_id: selectedId }),
      });
      detail.innerHTML = `
        ${session.has_audio ? `<audio controls preload="none" style="width:100%" src="${esc(`${audioTk.review_url}/audio/${id}?ticket=${audioTk.ticket}`)}"></audio>` : ""}
        ${
          session.summary
            ? `<p class="hint" style="margin-top:8px">Insights</p><ul style="padding-left:20px">${(session.summary.insights ?? [])
                .map((i) => `<li>${esc(i)}</li>`)
                .join("")}</ul>`
            : ""
        }
        ${session.corrected_script ? `<p class="hint">Their words, polished</p><p style="white-space:pre-wrap">${esc(session.corrected_script)}</p>` : ""}
        ${session.transcript ? `<p class="hint">Transcript</p><p style="white-space:pre-wrap;font-size:var(--text-sm)">${esc(session.transcript)}</p>` : `<p class="empty">No transcript stored.</p>`}`;
      tk.ticket = fresh.ticket; // keep the freshest ticket on the shared handle
    } catch (ex) {
      detail.innerHTML = `<p class="empty">${esc((ex as Error).message)}</p>`;
    }
  }

  function render(): void {
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";
    const flashHtml = flash ? `<div class="toast">${esc(flash)}</div>` : "";
    app.classList.add("lead-wide"); // Lead workspace widens on desktop (reset per-route in router.ts)
    app.innerHTML = `
      ${pageHeader({
        crumbs: [{ label: "Team", href: "/team" }, { label: "Term report" }],
        eyebrow: term?.name ?? "",
        title: "Term report",
        sub: "Each teacher's work this term. Private to you — coordination, not a scoreboard.",
      })}
      <div id="insroot">
        ${errHtml}${flashHtml}
        ${filterBar()}
        ${selectedId ? drillHtml() : overviewHtml()}
      </div>`;
    flash = "";

    const root = app.querySelector<HTMLElement>("#insroot")!;
    root.addEventListener("click", onClick);
    root.addEventListener("change", onChange);
    root.addEventListener("submit", onSubmit);

    // Tier-3 section loads lazily (it lives on the separate Review Worker, not this app's API).
    if (selectedId && !COACH_SET_ASIDE) void loadCoaching();
  }

  async function mutate(runFn: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await runFn();
      await load();
    } catch (e) {
      err = (e as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!el) return;
    const act = el.dataset.act;
    if (act === "open") { e.preventDefault(); selectedId = el.dataset.id ?? ""; void mutate(async () => {}); return; }
    if (act === "back") { e.preventDefault(); selectedId = ""; void mutate(async () => {}); return; }
    if (act === "print") { e.preventDefault(); window.print(); return; }
    if (act === "new-obs" && selectedId) {
      e.preventDefault();
      const kind = el.dataset.kind === "marking" ? "marking" : "classroom";
      void (async () => {
        try {
          const created = await api<{ id: string }>("/api/observations", {
            method: "POST",
            body: JSON.stringify({ teacher_id: selectedId, kind }),
          });
          navigate(`/observation/${created.id}`);
        } catch (ex) {
          err = (ex as Error)?.message ?? "Something went wrong.";
          render();
        }
      })();
      return;
    }
  }

  function onChange(e: Event): void {
    const sel = e.target as HTMLSelectElement | HTMLInputElement;
    if (sel.id === "f-teacher") { selectedId = sel.value; void mutate(async () => {}); return; }
    if (sel.id === "f-status") { statusFilter = sel.value; void mutate(async () => {}); return; }
    if (sel.id === "f-from") { weekFrom = sel.value; void mutate(async () => {}); return; }
    if (sel.id === "f-to") { weekTo = sel.value; void mutate(async () => {}); return; }
    if (sel.id === "f-q") { topicQ = sel.value.trim(); void mutate(async () => {}); return; }
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    if (form.id !== "track" || !selectedId) return;
    e.preventDefault();
    const fd = new FormData(form);
    const payload = {
      last_review: String(fd.get("last_review") ?? ""),
      last_observation: String(fd.get("last_observation") ?? ""),
      current_focus: String(fd.get("current_focus") ?? ""),
      next_action: String(fd.get("next_action") ?? ""),
    };
    void mutate(async () => {
      await api(`/api/lead/tracking/${encodeURIComponent(selectedId)}`, { method: "PUT", body: JSON.stringify(payload) });
      flash = "Notes saved.";
    });
  }

  await load();
  render();
}
